import { useState, useRef, useEffect, useMemo } from "react";
import api from "../services/api";

const DEFAULT_SETTINGS = {
  botName: "Heramba Ayurveda",
  subtitle: "WELLNESS GUIDE",
  greeting:
    "Namaste! I am your Heramba Ayurveda Wellness Guide. How can I assist you on your health and wellness journey today?",
  iconType: "cutout",
  iconEmoji: "🙏",
  iconUrl: "/assets/heramba-guide-cutout.png",
  fontFamily: "Inter",
  primaryColor: "#E8922A",
  secondaryColor: "#4A7C59",
  headerColor: "#14532D",
  tolerance: 0.7,
};

/* ─────────────────────────────────────────────
   Lightweight, dependency-free text similarity.
   Word-overlap score between 0 and 1 — no external
   library, no API call, runs entirely in-browser.
   Includes edit-distance so common typos still match
   (e.g. "namste" still matches "namaste").
───────────────────────────────────────────── */
function tokenize(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1);
}

// Levenshtein edit distance — counts insertions/deletions/substitutions
// needed to turn one word into another. Used to forgive typos.
function editDistance(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

// Two words are considered "the same word, typo'd" if their edit
// distance is small relative to word length (roughly 1 typo per 5 letters).
function isTypoMatch(a, b) {
  if (a === b) return true;
  const maxAllowed = Math.max(1, Math.floor(Math.min(a.length, b.length) / 4));
  return editDistance(a, b) <= maxAllowed;
}

function overlapScore(queryTokens, targetTokens) {
  if (!queryTokens.length || !targetTokens.length) return 0;
  const targetSet = new Set(targetTokens);
  let hits = 0;
  for (const t of queryTokens) {
    if (targetSet.has(t)) hits++;
    else if (targetTokens.some((tt) => tt.includes(t) || t.includes(tt))) hits += 0.6;
    else if (targetTokens.some((tt) => isTypoMatch(t, tt))) hits += 0.85;
  }
  return hits / queryTokens.length;
}

function findBestMatch(text, faqs, tolerance) {
  const queryTokens = tokenize(text);
  if (!queryTokens.length) return null;

  let best = null;
  let bestScore = 0;

  for (const faq of faqs) {
    const targetTokens = [
      ...tokenize(faq.question),
      ...faq.keywords.flatMap(tokenize),
    ];
    const score = overlapScore(queryTokens, targetTokens);
    if (score > bestScore) {
      bestScore = score;
      best = faq;
    }
  }

  // tolerance 0 (strict) → need near-full overlap (~0.75)
  // tolerance 1 (loose)  → accept as low as ~0.2
  const threshold = 0.75 - tolerance * 0.55;
  return bestScore >= threshold ? best : null;
}

const FALLBACK =
  "I'm not sure about that one yet — but I can tell you about our treatments, doctors, consultations, or clinic timing. You can also reach our team directly through the Request page for anything else.";

const PHYSICIAN_ART_ID = "hrm-physician-bob-anim";

function PhysicianIllustration({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Ayurvedic physician icon">
      <style>{`
        @keyframes ${PHYSICIAN_ART_ID} { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-3px); } }
        .${PHYSICIAN_ART_ID} { animation: ${PHYSICIAN_ART_ID} 2.8s ease-in-out infinite; transform-origin: center; }
      `}</style>
      <circle cx="50" cy="50" r="48" fill="#F5EDD8" />
      <g className={PHYSICIAN_ART_ID}>
        <path d="M 30 62 Q 50 50 70 62 L 72 85 Q 50 92 28 85 Z" fill="#4A7C59" />
        <ellipse cx="50" cy="48" rx="17" ry="19" fill="#E8B084" />
        <path
          d="M 33 44 Q 30 62 38 70 Q 50 76 62 70 Q 70 62 67 44 Q 63 58 50 60 Q 37 58 33 44 Z"
          fill="#F2F2F0"
        />
        <path d="M 30 36 Q 30 20 50 18 Q 70 20 70 36 Q 70 30 50 29 Q 30 30 30 36 Z" fill="#E8922A" />
        <ellipse cx="50" cy="26" rx="21" ry="12" fill="#E8922A" />
        <circle cx="50" cy="22" r="4" fill="#F7C948" />
        <circle cx="43" cy="45" r="1.6" fill="#2C1810" />
        <circle cx="57" cy="45" r="1.6" fill="#2C1810" />
        <path d="M 44 53 Q 50 56 56 53" fill="none" stroke="#B86A10" strokeWidth="1.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function Avatar({ size, settings }) {
  if (settings.iconType === "illustrated") {
    return <PhysicianIllustration size={size} />;
  }
  if (settings.iconType === "cutout" && settings.iconUrl) {
    // Full-body cutout doesn't suit a tiny circle well — crop tight on the
    // face by scaling the image up and anchoring to the top inside the circle.
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          overflow: "hidden",
          flexShrink: 0,
          background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})`,
        }}
      >
        <img
          src={settings.iconUrl}
          alt=""
          style={{
            width: "210%",
            height: "210%",
            objectFit: "cover",
            objectPosition: "50% 8%",
            marginLeft: "-55%",
          }}
        />
      </div>
    );
  }
  const showImg = settings.iconType === "upload" && settings.iconUrl;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: showImg
          ? "transparent"
          : `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: size * 0.5,
        overflow: "hidden",
      }}
    >
      {showImg ? (
        <img src={settings.iconUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        settings.iconEmoji
      )}
    </div>
  );
}

export default function WellnessGuideBot() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [faqs, setFaqs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [ready, setReady] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/chatbot/settings").catch(() => ({ data: DEFAULT_SETTINGS })),
      api.get("/chatbot/faq").catch(() => ({ data: [] })),
    ]).then(([settingsRes, faqRes]) => {
      const s = { ...DEFAULT_SETTINGS, ...settingsRes.data };
      setSettings(s);
      setFaqs(faqRes.data || []);
      setMessages([{ from: "bot", text: s.greeting }]);
      setReady(true);
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const quickReplies = useMemo(() => faqs.filter((f) => f.isQuickReply).slice(0, 4), [faqs]);

  const respond = (text) => {
    const match = findBestMatch(text, faqs, settings.tolerance);
    setTimeout(() => {
      if (match) {
        setMessages((m) => [
          ...m,
          { from: "bot", text: match.answer, followUps: match.followUps?.length ? match.followUps : null },
        ]);
      } else {
        setMessages((m) => [...m, { from: "bot", text: FALLBACK }]);
        api.post("/chatbot/log-unanswered", { question: text }).catch(() => {});
      }
    }, 300);
  };

  const sendText = (text) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { from: "user", text }]);
    respond(text);
    setInput("");
  };

  const sendQuickReply = (faq) => {
    setMessages((m) => [...m, { from: "user", text: faq.question }]);
    setTimeout(
      () =>
        setMessages((m) => [
          ...m,
          { from: "bot", text: faq.answer, followUps: faq.followUps?.length ? faq.followUps : null },
        ]),
      300
    );
  };

  const sendFollowUp = (fu) => {
    setMessages((m) => [...m, { from: "user", text: fu.label }]);
    setTimeout(() => setMessages((m) => [...m, { from: "bot", text: fu.answer }]), 300);
  };

  if (!ready) return null;

  const fontStack = `'${settings.fontFamily}', sans-serif`;

  return (
    // bottom: 104 clears the WhatsApp button (bottom:28 + 62px tall + ~14px gap)
    <div style={{ position: "fixed", bottom: 104, right: 24, zIndex: 9999 }}>
      <style>{`
        #hrm-qr-track::-webkit-scrollbar { display: none; }
        #hrm-qr-track { scrollbar-width: none; }
        @media (max-width: 480px) {
          #hrm-chat-panel { height: 68vh !important; }
        }
      `}</style>
      {open && (
        <div
          id="hrm-chat-panel"
          style={{
            width: 340,
            maxWidth: "92vw",
            height: 480,
            maxHeight: "75vh",
            background: "#fff",
            borderRadius: 18,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            marginBottom: 14,
            fontFamily: fontStack,
          }}
        >
          <div
            style={{
              background: settings.headerColor,
              color: "#fff",
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Avatar size={36} settings={settings} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{settings.botName}</div>
              <div style={{ fontSize: 11, letterSpacing: "0.08em", color: settings.primaryColor, fontWeight: 600 }}>
                {settings.subtitle}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", opacity: 0.7 }}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "#FDFAF3" }}>
            {messages.map((m, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexDirection: m.from === "bot" ? "row" : "row-reverse",
                  }}
                >
                  {m.from === "bot" && <Avatar size={28} settings={settings} />}
                  <div
                    style={{
                      background: m.from === "bot" ? "#fff" : settings.primaryColor,
                      color: m.from === "bot" ? "#2C1810" : "#fff",
                      padding: "10px 13px",
                      borderRadius: 14,
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      maxWidth: "78%",
                      boxShadow: m.from === "bot" ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    {m.text}
                  </div>
                </div>

                {m.from === "bot" && m.followUps && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 8,
                      marginLeft: 36,
                    }}
                  >
                    {m.followUps.map((fu, fi) => (
                      <button
                        key={fi}
                        onClick={() => sendFollowUp(fu)}
                        style={{
                          background: "#fff",
                          color: settings.primaryColor,
                          border: `1.5px solid ${settings.primaryColor}`,
                          borderRadius: 20,
                          padding: "6px 13px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fu.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {quickReplies.length > 0 && (
            <div
              id="hrm-qr-track"
              style={{
                display: "flex",
                gap: 8,
                padding: "10px 12px",
                flexWrap: "nowrap",
                overflowX: "auto",
                borderTop: "0.5px solid #F5EDD8",
                background: "#fff",
              }}
            >
              {quickReplies.map((faq) => (
                <button
                  key={faq._id}
                  onClick={() => sendQuickReply(faq)}
                  style={{
                    background: `${settings.primaryColor}18`,
                    color: settings.primaryColor,
                    border: `1px solid ${settings.primaryColor}55`,
                    borderRadius: 20,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {faq.question}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "0.5px solid #F5EDD8" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText(input)}
              placeholder="Ask about treatment, timing, doctors..."
              style={{
                flex: 1,
                border: "1px solid #F5EDD8",
                borderRadius: 20,
                padding: "9px 14px",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button
              onClick={() => sendText(input)}
              style={{
                background: settings.primaryColor,
                border: "none",
                borderRadius: "50%",
                width: 36,
                height: 36,
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 15,
              }}
              aria-label="Send"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {settings.iconType === "cutout" ? (
        <div
          onClick={() => setOpen((o) => !o)}
          style={{
            position: "relative",
            width: 110,
            marginLeft: "auto",
            cursor: "pointer",
            display: open ? "none" : "block",
          }}
          aria-label="Open Wellness Guide chat"
        >
          <img
            src={settings.iconUrl}
            alt="Chat with us"
            style={{
              width: "100%",
              display: "block",
              filter: "drop-shadow(0 8px 14px rgba(0,0,0,0.35))",
            }}
          />
          <span
            style={{
              position: "absolute",
              bottom: "6%",
              right: "8%",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#22c55e",
              border: "2.5px solid #fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
            }}
          />
        </div>
      ) : (
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            border: "none",
            background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})`,
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
            cursor: "pointer",
            fontSize: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "auto",
            overflow: "hidden",
          }}
          aria-label="Open Wellness Guide chat"
        >
          {open ? (
            "×"
          ) : settings.iconType === "illustrated" ? (
            <PhysicianIllustration size={60} />
          ) : settings.iconType === "upload" && settings.iconUrl ? (
            <img src={settings.iconUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            settings.iconEmoji
          )}
        </button>
      )}
    </div>
  );
}